import React, { useState } from 'react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import {
  Sparkles,
  ShieldCheck,
  Loader2,
  AlertTriangle,
  FolderSync,
  BarChart3,
  Timer,
  CalendarDays,
} from 'lucide-react';

export const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Formulario alternativo de email / contraseña
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      const redirectUri = window.location.origin + '/auth/callback';
      const res = await api.getGoogleLoginUrl(redirectUri);
      if (res.url) {
        window.location.href = res.url;
      }
    } catch (err: any) {
      console.error('Error al iniciar sesión con Google:', err);
      setError(
        err.message ||
          'No se pudo conectar con el servidor de autenticación. Verifica que el backend esté en ejecución.'
      );
      setLoading(false);
    }
  };

  const handleCredentialsLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Por favor ingresa tu correo y contraseña.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await api.loginWithCredentials(email, password);
      if (res.success && res.session_token) {
        useAuthStore.getState().login(res.user, res.session_token);
        window.location.href = '/personal';
      }
    } catch (err: any) {
      setError(err.message || 'Credenciales inválidas.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-900 via-purple-950 to-slate-950 flex flex-col justify-between p-6 sm:p-10 relative overflow-hidden">
      {/* Círculos decorativos de fondo con blur */}
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl pointer-events-none"></div>

      {/* Barra superior minimalista */}
      <div className="flex items-center justify-between z-10">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-purple-500 to-indigo-500 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-purple-500/20">
            T
          </div>
          <span className="text-white font-semibold text-base tracking-tight">
            Teresita <span className="text-purple-400 font-normal">Marketing</span>
          </span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/10 text-white/70 text-xs">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
          <span>Acceso Privado</span>
        </div>
      </div>

      {/* Tarjeta Central de Acceso */}
      <div className="max-w-md w-full mx-auto my-auto z-10 py-8">
        <div className="bg-white/95 backdrop-blur-xl border border-white/20 rounded-3xl p-8 sm:p-10 shadow-2xl shadow-purple-950/50 text-center transition-all">
          {/* Logo / Ícono animado */}
          <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-gradient-to-tr from-purple-500 via-indigo-500 to-pink-500 text-white flex items-center justify-center shadow-xl shadow-purple-500/30">
            <Sparkles className="w-8 h-8 animate-pulse" />
          </div>

          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            Bienvenida de vuelta
          </h1>
          <p className="text-slate-500 text-xs sm:text-sm mt-2 mb-6 leading-relaxed">
            Inicia sesión con tu usuario personal. La conexión de Google Drive del equipo permanece intacta.
          </p>

          {!showPasswordLogin ? (
            <div className="space-y-4">
              {/* Botón Principal: Iniciar sesión con Google */}
              <button
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 active:scale-[0.98] text-slate-800 font-semibold text-sm shadow-md hover:shadow-lg transition-all group"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                    <span>Conectando con Google...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                      />
                    </svg>
                    <span className="text-slate-700 group-hover:text-slate-900 transition-colors">
                      Continuar con Google
                    </span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => setShowPasswordLogin(true)}
                className="text-xs text-purple-700 hover:text-purple-900 font-medium hover:underline block mx-auto pt-1"
              >
                O ingresar con correo y contraseña
              </button>
            </div>
          ) : (
            <form onSubmit={handleCredentialsLogin} className="space-y-3 text-left">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Correo electrónico
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ejemplo@gmail.com"
                  required
                  className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Contraseña o clave de acceso
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold text-xs shadow-md transition-all flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Entrar al Panel'}
              </button>

              <button
                type="button"
                onClick={() => setShowPasswordLogin(false)}
                className="text-xs text-slate-500 hover:text-slate-800 font-medium hover:underline block mx-auto pt-1"
              >
                ← Volver a ingresar con Google
              </button>
            </form>
          )}

          {/* Nota informativa de separación con Google Drive */}
          <div className="mt-5 p-2.5 rounded-xl bg-purple-50/70 border border-purple-100 text-[11px] text-purple-900 text-left leading-snug">
            <p className="font-semibold flex items-center gap-1.5 text-purple-950">
              <ShieldCheck className="w-3.5 h-3.5 text-purple-600 shrink-0" />
              Sesión y Google Drive separados
            </p>
            <p className="mt-0.5 text-purple-800 text-[10px]">
              Cada miembro del equipo puede iniciar sesión con su propio usuario. La cuenta de Google Drive conectada no se alterará ni se desconectará.
            </p>
          </div>

          {/* Alerta de error si aplica */}
          {error && (
            <div className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs text-left flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">No se pudo acceder</p>
                <p className="text-[11px] text-rose-700 mt-0.5 leading-snug">{error}</p>
              </div>
            </div>
          )}

          {/* Características activas */}
          <div className="mt-8 pt-6 border-t border-slate-100 grid grid-cols-2 gap-3 text-left">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <FolderSync className="w-4 h-4 text-purple-600" />
              <span>Drive personal</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <BarChart3 className="w-4 h-4 text-indigo-600" />
              <span>Métricas de Meta</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <Timer className="w-4 h-4 text-pink-600" />
              <span>Control de tiempo</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <CalendarDays className="w-4 h-4 text-emerald-600" />
              <span>Kanban & Calendario</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pie de página */}
      <div className="text-center text-xs text-white/50 z-10">
        <p>Tu sesión se mantiene segura y activa en este dispositivo durante 30 días.</p>
        <p className="mt-1 text-[11px] text-white/30">v1.0 — Hecho a medida con 💜 para Teresita</p>
      </div>
    </div>
  );
};
